import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Progress,
  Typography,
  Row,
  Col,
  InputNumber,
} from "antd";
import {
  EditOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  DeleteOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getSalesOrderById, deleteSalesOrder, updateOrderItemFulfillment } from "../../../redux/actions/sale/salesOrderAction";
import SalesOrderPrint from "./SalesOrderPrint";

const { Title } = Typography;

const SalesOrderDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const order = useSelector((state) => state.salesOrders.currentOrder);
  const [editedItems, setEditedItems] = useState({});

  useEffect(() => {
    dispatch(getSalesOrderById(id));
  }, [dispatch, id]);

  const getDisplayValue = (record, field) =>
    editedItems[record.id]?.[field] !== undefined && editedItems[record.id]?.[field] !== null
      ? editedItems[record.id][field]
      : record[field];

  const setItemEdit = (itemId, field, value) => {
    setEditedItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value },
    }));
  };

  const handleSaveFulfillment = async () => {
    const items = Object.entries(editedItems)
      .filter(([, e]) => Object.keys(e).length > 0)
      .map(([itemId, e]) => ({ item_id: Number(itemId), ...e }));
    if (items.length === 0) return;
    const result = await dispatch(updateOrderItemFulfillment(id, { items }));
    if (result?.message === "success") setEditedItems({});
  };

  const hasEdits = Object.keys(editedItems).length > 0;

  if (!order) {
    return <div>Loading...</div>;
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: "orange",
      partial: "blue",
      fulfilled: "green",
      cancelled: "red",
    };
    return colors[status] || "default";
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this sales order?")) {
      const result = await dispatch(deleteSalesOrder(id));
      if (result?.message === "success") {
        navigate("/sales-order");
      }
    }
  };

  const handleCreatePurchaseOrder = () => {
    navigate(`/purchase-order/add?sales_order_id=${id}`);
  };

  const handleCreateSaleInvoice = () => {
    navigate(`/sale?sales_order_id=${id}`);
  };

  const itemColumns = [
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (product) => (
        <div>
          <div>{product?.name || "-"}</div>
          <div style={{ fontSize: "12px", color: "#999" }}>ISBN: {product?.isbn || "-"}</div>
        </div>
      ),
    },
    {
      title: "Ordered Qty",
      dataIndex: "ordered_quantity",
      key: "ordered_quantity",
    },
    {
      title: "Fulfilled Qty",
      dataIndex: "fulfilled_quantity",
      key: "fulfilled_quantity",
      render: (_, record) => (
        <InputNumber
          min={0}
          max={record.ordered_quantity}
          value={getDisplayValue(record, "fulfilled_quantity")}
          onChange={(val) => setItemEdit(record.id, "fulfilled_quantity", val ?? 0)}
          style={{ width: "80px" }}
        />
      ),
    },
    {
      title: "Pending Qty",
      key: "pending",
      render: (_, record) => record.ordered_quantity - (getDisplayValue(record, "fulfilled_quantity") ?? 0),
    },
    {
      title: "Price",
      dataIndex: "sale_price",
      key: "sale_price",
      render: (_, record) => (
        <InputNumber
          min={0}
          step={0.01}
          prefix="₹"
          value={getDisplayValue(record, "sale_price")}
          onChange={(val) => setItemEdit(record.id, "sale_price", val ?? 0)}
          style={{ width: "120px" }}
        />
      ),
    },
    {
      title: "Fulfillment",
      key: "fulfillment",
      render: (_, record) => {
        const fulfilled = getDisplayValue(record, "fulfilled_quantity") ?? 0;
        const percentage = record.ordered_quantity > 0 ? (fulfilled / record.ordered_quantity) * 100 : 0;
        return (
          <Progress
            percent={Math.round(percentage)}
            status={percentage === 100 ? "success" : percentage > 0 ? "active" : "normal"}
            size="small"
          />
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: "20px" }}>
          <Title level={3} style={{ margin: 0 }}>
            Sales Order Details
          </Title>
          <Space wrap>
            <SalesOrderPrint order={order} />
            {order.status !== "fulfilled" && order.status !== "cancelled" && (
              <>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/sales-order/edit/${id}`)}
                >
                  Edit
                </Button>
                <Button
                  type="primary"
                  icon={<ShoppingCartOutlined />}
                  onClick={handleCreatePurchaseOrder}
                >
                  Create Purchase Order
                </Button>
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={handleCreateSaleInvoice}
                >
                  Generate Invoice
                </Button>
              </>
            )}
            {order.status !== "fulfilled" && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>

        <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Order Number">{order.order_number}</Descriptions.Item>
          <Descriptions.Item label="Order Date">
            {moment(order.order_date).format("DD-MM-YYYY")}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(order.status)}>{order.status.toUpperCase()}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Customer">{order.customer?.name || "-"}</Descriptions.Item>
          <Descriptions.Item label="Sales Person">{order.user?.username || "-"}</Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            ₹{parseFloat(order.total_amount || 0).toFixed(2)}
          </Descriptions.Item>
          {order.note && (
            <Descriptions.Item label="Note" span={3}>
              {order.note}
            </Descriptions.Item>
          )}
        </Descriptions>

        <div style={{ marginTop: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Title level={4} style={{ margin: 0 }}>Order Items</Title>
            {hasEdits && (
              <Button type="primary" onClick={handleSaveFulfillment}>
                Save Fulfillment & Price
              </Button>
            )}
          </div>
          <Table
            columns={itemColumns}
            dataSource={order.order_items || []}
            rowKey="id"
            pagination={false}
          />
        </div>

        {order.purchaseInvoices && order.purchaseInvoices.length > 0 && (
          <div style={{ marginTop: "24px" }}>
            <Title level={4}>Linked Purchase Invoices</Title>
            <Table
              columns={[
                { title: "Invoice ID", dataIndex: "id", key: "id" },
                { title: "Date", dataIndex: "date", key: "date", render: (date) => moment(date).format("DD-MM-YYYY") },
                { title: "Amount", dataIndex: "total_amount", key: "total_amount", render: (amt) => `₹${parseFloat(amt || 0).toFixed(2)}` },
              ]}
              dataSource={order.purchaseInvoices}
              rowKey="id"
              pagination={false}
            />
          </div>
        )}

        {order.saleInvoices && order.saleInvoices.length > 0 && (
          <div style={{ marginTop: "24px" }}>
            <Title level={4}>Linked Sale Invoices</Title>
            <Table
              columns={[
                { title: "Invoice Number", dataIndex: "invoice_number", key: "invoice_number", render: (num, record) => `${record.prefix || ""}${num}` },
                { title: "Date", dataIndex: "date", key: "date", render: (date) => moment(date).format("DD-MM-YYYY") },
                { title: "Amount", dataIndex: "total_amount", key: "total_amount", render: (amt) => `₹${parseFloat(amt || 0).toFixed(2)}` },
              ]}
              dataSource={order.saleInvoices}
              rowKey="id"
              pagination={false}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default SalesOrderDetails;
